// @flow
/** @jsx jsx */

import { jsx } from '@emotion/core';
import * as React from 'react';
import Glyphicon from 'react-bootstrap/lib/Glyphicon';
import { FlexGridXlsxConverter } from 'wijmo/wijmo.grid.xlsx';

import type { CommonActionProps } from './types.actions';
import ButtonAction from './ButtonAction';
import Prompt from '../Prompt';
import messages from '../../config/messages';
import { generateFileName } from '../../utils';
import { DesktopView, MobileView } from '../../config/mediaQuery';

const MAX_ITEMS_TO_EXPORT = 360000;
const BATCH_SIZE = 20;

const saveFile = async ({ grid, fileName, pageSize, pageIndex, setExportProgress, exportWithStyles = true }) => {
  return await new Promise((resolve, reject) => {
    FlexGridXlsxConverter.saveAsync(
      grid,
      {
        includeColumnHeaders: true,
        includeStyles: exportWithStyles,
        includeColumns: column => column.visible,
      },
      fileName,
      () => {
        grid.itemsSource.pageSize = pageSize;
        grid.itemsSource.moveToPage(pageIndex);

        if (setExportProgress) {
          setExportProgress(null)
        }
        resolve();
      },
      e => {
        grid.itemsSource.pageSize = pageSize;
        grid.itemsSource.moveToPage(pageIndex);

        reject(e);
      },
      (progressValue) => {
        if (setExportProgress && Number(progressValue) % 5 === 0) {
          setExportProgress(progressValue);
        }
      },
      true, // accesible grid when exporting,
      BATCH_SIZE
    );
  });
};

const yieldToUI = () => new Promise((resolve) => requestAnimationFrame(resolve))

const exportToExcel = async (grid: any, fileName: string, setExportProgress: (number | null) => void, exportWithStyles: boolean) => {
  if (grid) {
    return new Promise((resolve, reject) => {
      const { pageSize, pageIndex } = grid.itemsSource;

      grid.collectionView.collectionChanged.addHandler(async function loadHandler() {
        grid.collectionView.collectionChanged.removeHandler(loadHandler);

        try {
          await yieldToUI()
          await saveFile({
            grid,
            fileName,
            pageSize,
            pageIndex,
            setExportProgress,
            exportWithStyles,
          })
          await yieldToUI()

          resolve();
        } catch (e) {
          reject(e);
        }
      });

      /**
       * We need to import all the content, so without this, we'll be
       * exporting only the current page. This change in pageSize will
       * trigger the even handler above.
       */
      requestAnimationFrame(() => {
        grid.itemsSource.pageSize = grid.itemsSource.totalItemCount;
      });
    });
  }
};

type Props = {
  moduleName: string,
  grid: any,
  disabled?: boolean,
  disabledDelay: number,
  onExporting: (any, string, () => mixed, (any) => mixed) => mixed,
  onExported: (any, string, () => mixed, (any) => mixed) => mixed,
  setExportProgress?: (number | null) => void,
  exportWithStyles?: boolean,
};

type State = {
  status: string,
};

class ExportODataAction extends React.PureComponent<
  CommonActionProps & Props,
  State,
> {
  static defaultProps = {
    size: 'xsmall',
    disabledDelay: 500,
    exportToExcel,
    disabled: false,
  };

  state = {
    status: '',
  };

  exportToExcel = (options: { grid: any, moduleName: string }) => () => {
    const { grid, moduleName } = options;
    const fileName = generateFileName(moduleName);

    if (
      grid &&
      grid.collectionView &&
      grid.collectionView.totalItemCount <= MAX_ITEMS_TO_EXPORT
    ) {
      this.props.onExporting('exporting', async () => {
        /**
         * The exportToExcel is an extremely intense an blocking action, so we need
         * to give React some space to perform the update. If we don't wait for a bit
         * before triggering that action we might not even be able to show it.
         */
        await new Promise((resolve) => setTimeout(resolve, 100))

        await exportToExcel(grid, fileName, this.props.setExportProgress, this.props.exportWithStyles);
        this.props.onExported(grid, fileName);
      });
    } else {
      this.setState({
        status: 'limit-exceeded',
      });
    }
  };

  onConfirmLimitExceeded = () => {
    this.setState({
      status: '',
    });
  };

  render() {
    const disabled = this.state.status !== '' || this.props.disabled;

    return (
      <React.Fragment>
        <DesktopView>
          <ButtonAction
            onClick={this.exportToExcel({
              grid: this.props.grid,
              moduleName: this.props.moduleName,
            })}
            bsSize={this.props.size}
            disabled={disabled}
          >
            <Glyphicon glyph="file" /> Export
          </ButtonAction>
          {this.state.status === 'limit-exceeded' && (
            <Prompt
              onConfirm={this.onConfirmLimitExceeded}
              message={messages.export.limitExceeded}
            />
          )}
        </DesktopView>
        <MobileView>
          <button
            onClick={this.exportToExcel({
              grid: this.props.grid,
              moduleName: this.props.moduleName,
            })}
            disabled={disabled}
          >
            <img src='assets/file-bottom.svg' alt='file icon'/> Export
          </button>
        </MobileView>
      </React.Fragment>
    );
  }
}

export default ExportODataAction;
